import type { ID } from "types";
import { ApiRoutes } from "consts/enpoints/api";
import { Filters } from "consts/users";
import { Filters as MerchantFilters } from "consts/merchants";
import { FilterValueModifier } from "consts/filters";
import { RoleName } from "consts/auth";
import { RequestQueryMapper } from "util/request-query-mapper";
import { fetchApi } from "infrastructure/api";
import { updateOrCreateResource } from "infrastructure/api/api";
import type {
    Ledger,
    Merchant,
    MerchantBusiness,
    MerchantDomain,
    MerchantKYCService,
    MerchantPayoutAccount,
    MerchantPayoutCryptoAccount,
    MerchantSettings
} from "features/merchants/types";

const COLLECTION_ID_SEPARATOR = '[in][]';
const NO_MATCH_ID = '-1';

/**
 * The PSP filter (enabledAssignablePaymentMethodPSP) is applied by GET /merchants.
 * apiId lives on merchant settings and is not filterable on /merchants yet,
 * so it is resolved server-side through /merchant-settings into a coreId filter.
 */
export const getMerchants = async (queryString = '') => {
    const query = new URLSearchParams(queryString);
    const apiId = query.get(MerchantFilters.apiId);

    if (apiId) {
        query.delete(MerchantFilters.apiId);

        const coreIdKey = `${MerchantFilters.coreId}${COLLECTION_ID_SEPARATOR}`;
        const selectedCoreIds = query.getAll(coreIdKey);
        const apiIdMerchantIds = await getMerchantIdsByApiId(apiId);
        const coreIds = selectedCoreIds.length
            ? apiIdMerchantIds.filter(id => selectedCoreIds.includes(id))
            : apiIdMerchantIds;

        query.delete(coreIdKey);
        (coreIds.length ? coreIds : [NO_MATCH_ID])
            .forEach(id => query.append(coreIdKey, id));
    }

    const merchantsResponse = await fetchApi(`${ApiRoutes.GetMerchants}?${query.toString()}`);

    if (!merchantsResponse.ok) {
        return merchantsResponse;
    }

    const payload = await merchantsResponse.json();
    const merchants: Array<Merchant> = payload.data?.data ?? [];
    const apiIdByMerchant = await getApiIdsByMerchantIds(
        merchants.map(({ coreId }) => String(coreId))
    );

    payload.data.data = merchants.map(merchant => ({
        ...merchant,
        apiId: apiIdByMerchant.get(String(merchant.coreId)) ?? null
    }));

    return new Response(JSON.stringify(payload), {
        status: merchantsResponse.status,
        headers: { 'Content-Type': 'application/json' }
    });
};

const getMerchantSettingsData = async (query: URLSearchParams): Promise<Array<MerchantSettings>> => {
    const response = await getMerchantSettings(`?${query.toString()}`);

    if (!response.ok) {
        return [];
    }

    return (await response.json()).data?.data ?? [];
};

const getMerchantIdsByApiId = async (apiId: string) => {
    // `apiId=<value>` would be a "starts with" match; [o]=contains matches anywhere
    const settings = await getMerchantSettingsData(new URLSearchParams([
        [`${MerchantFilters.apiId}[o]`, FilterValueModifier.Contains],
        [`${MerchantFilters.apiId}[v]`, apiId],
        ['perPage', '100']
    ]));

    return settings.map(({ merchantId }) => String(merchantId));
};

const getApiIdsByMerchantIds = async (merchantIds: Array<string>) => {
    if (!merchantIds.length) {
        return new Map<string, MerchantSettings['apiId']>();
    }

    const settings = await getMerchantSettingsData(new URLSearchParams([
        ...merchantIds.map(id => [`merchantId${COLLECTION_ID_SEPARATOR}`, id]),
        ['perPage', String(merchantIds.length)]
    ]));

    return new Map(settings.map(({ merchantId, apiId }) => [String(merchantId), apiId]));
};

export const getMerchantById = (coreId: ID) =>
    fetchApi(ApiRoutes.GetMerchant(coreId));

export const getMerchantDomains = (queryString = '') =>
    fetchApi(`${ApiRoutes.GetMerchantDomains}${queryString}`);

export const createOrUpdateMerchantDomain = (body: Partial<MerchantDomain>) =>
    updateOrCreateResource({
        body,
        getRoute: ApiRoutes.PostMerchantDomains,
        getId: body => body.coreId
    });

export const deleteMerchantDomain = (coreId: ID) =>
    fetchApi(ApiRoutes.DeleteMerchantDomains(coreId), {
        method: 'DELETE'
    });

export const getMerchantBusinesses = (queryString = '') =>
    fetchApi(`${ApiRoutes.GetMerchantBusinesses()}${queryString}`);

export const getMerchantBusinessById = (coreId: ID) =>
    fetchApi(ApiRoutes.GetMerchantBusinesses(coreId));

export const createOrUpdateMerchantBusiness = (body: Partial<MerchantBusiness>) =>
    updateOrCreateResource({
        body,
        getRoute: ApiRoutes.PostMerchantBusinesses,
        getId: body => body.coreId
    });

export const deleteMerchantBusiness = (coreId: ID) =>
    fetchApi(ApiRoutes.DeleteMerchantBusinesses(coreId), {
        method: 'DELETE'
    });

export const getMerchantUsers = (queryString = '') =>
    fetchApi(`${ApiRoutes.GetMerchantUsers}${
        RequestQueryMapper
            .from(queryString)
            .contains(Filters.roleName, RoleName.Merchant)
            .toString()
    }`);

export const getMerchantGroups = (queryString = '') =>
    fetchApi(`${ApiRoutes.GetMerchantGroups}${queryString}`);

export const createOrUpdateMerchant = (body: Partial<Merchant>) =>
    updateOrCreateResource({
        body,
        getRoute: ApiRoutes.PostMerchant,
        getId: body => body.coreId
    });

export const getMerchantKYCSettings = (queryString = '') =>
    fetchApi(`${ApiRoutes.GetMerchantKYCSettings}${queryString}`);

export const updateOrCreateMerchantKYCSettings = (body: Partial<MerchantKYCService> & Required<Pick<
    MerchantKYCService,
    | 'merchantId'
    | 'kycTypeId'
    | 'summedAmountEurLimit'
>>) =>
    fetchApi(ApiRoutes.PutMerchantKYCSettings(
        body.merchantId,
        body.kycTypeId
    ), {
        method: 'PUT',
        body
    });

export const getMerchantSettings = (queryString = '') =>
    fetchApi(`${ApiRoutes.GetMerchantSettings}${queryString}`);

export const createOrUpdateMerchantSettings = (body: Partial<MerchantSettings>) =>
    updateOrCreateResource({
        body,
        getRoute: ApiRoutes.PutMerchantSettings,
        getId: body => body.coreId
    });

export const getPayoutAccounts = (queryString = '') =>
    fetchApi(`${ApiRoutes.GetPayoutAccounts}${queryString}`);

export const createOrUpdatePayoutAccount = (body: Partial<MerchantPayoutAccount>) =>
    updateOrCreateResource({
        body,
        getRoute: ApiRoutes.PutPayoutAccounts,
        getId: body => body.coreId
    });

export const deletePayoutAccount = (coreId: ID) =>
    fetchApi(ApiRoutes.DeletePayoutAccounts(coreId), {
        method: 'DELETE'
    });

export const getPayoutCryptoAccounts = (queryString = '') =>
    fetchApi(`${ApiRoutes.GetPayoutCryptoAccounts}${queryString}`);

export const createOrUpdatePayoutCryptoAccount = (body: Partial<MerchantPayoutCryptoAccount>) =>
    updateOrCreateResource({
        body,
        getRoute: ApiRoutes.PutPayoutCryptoAccounts,
        getId: body => body.coreId
    });

export const deletePayoutCryptoAccount = (coreId: ID) =>
    fetchApi(ApiRoutes.DeletePayoutCryptoAccounts(coreId), {
        method: 'DELETE'
    });

export const getLedgers = (queryString = '') =>
    fetchApi(`${ApiRoutes.GetLedgers}${queryString}`);

export const createOrUpdateLedger = (body: Partial<Ledger>) =>
    updateOrCreateResource({
        body,
        getRoute: ApiRoutes.PostLedgers,
        getId: body => body.coreId
    });
